import { useState, useRef, useEffect } from "react";

interface Message {
  id: number;
  from: "bot" | "user";
  text: string;
  options?: Option[];
}

interface Option {
  label: string;
  value: string;
}

const FAQS: Record<string, { answer: string; followUp?: Option[] }> = {
  track: {
    answer:
      "To track your parcel, enter your tracking number (format: SE-000000000) in the search bar on the homepage and hit Track. You'll see real-time status, current location, and delivery details.",
    followUp: [
      { label: "What if my tracking number doesn't work?", value: "no_track" },
      { label: "Back to main menu", value: "menu" },
    ],
  },
  no_track: {
    answer:
      "If your tracking number isn't found, double-check it with the sender — it may not have been registered yet. Tracking numbers are activated once the parcel is physically received at our facility.",
    followUp: [
      { label: "Contact live support", value: "contact" },
      { label: "Back to main menu", value: "menu" },
    ],
  },
  delay: {
    answer:
      "Delays can happen due to customs clearance, high shipment volumes, weather, or public holidays. If your parcel is past its estimated delivery date, our team can investigate.",
    followUp: [
      { label: "Contact live support", value: "contact" },
      { label: "Back to main menu", value: "menu" },
    ],
  },
  customs: {
    answer:
      "Customs clearance is managed by the destination country's authorities and can take 2–7 business days. You or the recipient may need to provide documents or pay import duties. We'll notify you if action is needed.",
    followUp: [
      { label: "Contact live support", value: "contact" },
      { label: "Back to main menu", value: "menu" },
    ],
  },
  address: {
    answer:
      "Address changes are possible if the parcel hasn't been dispatched for final delivery yet. Please contact our support team as soon as possible — changes can't be guaranteed once it's out for delivery.",
    followUp: [
      { label: "Contact live support", value: "contact" },
      { label: "Back to main menu", value: "menu" },
    ],
  },
  missed: {
    answer:
      "If a delivery attempt was missed, our courier will typically retry the next business day and leave a notice. You may also be able to collect from a nearby depot. Contact support to arrange redelivery.",
    followUp: [
      { label: "Contact live support", value: "contact" },
      { label: "Back to main menu", value: "menu" },
    ],
  },
  contact: {
    answer:
      "Our support team is available via email. Click below to open your email app — your message will be pre-filled so you can send it in seconds.",
    followUp: [
      { label: "Back to main menu", value: "menu" },
    ],
  },
  menu: {
    answer: "Sure! Here's what I can help you with:",
    followUp: [
      { label: "How do I track my parcel?", value: "track" },
      { label: "My parcel is delayed", value: "delay" },
      { label: "Parcel stuck at customs", value: "customs" },
      { label: "Change delivery address", value: "address" },
      { label: "Missed delivery attempt", value: "missed" },
      { label: "Contact live support", value: "contact" },
    ],
  },
};

const MAIN_MENU_OPTIONS: Option[] = [
  { label: "How do I track my parcel?", value: "track" },
  { label: "My parcel is delayed", value: "delay" },
  { label: "Parcel stuck at customs", value: "customs" },
  { label: "Change delivery address", value: "address" },
  { label: "Missed delivery attempt", value: "missed" },
  { label: "Contact live support", value: "contact" },
];

const SUPPORT_EMAIL = "sonicexpresservices@gmail.com";

let msgId = 0;
function nextId() { msgId += 1; return msgId; }

function buildBotMessage(text: string, options?: Option[]): Message {
  return { id: nextId(), from: "bot", text, options };
}

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    buildBotMessage(
      "Hi there! I'm the Sonic Express virtual assistant. How can I help you today?",
      MAIN_MENU_OPTIONS
    ),
  ]);
  const [showEmailCard, setShowEmailCard] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
    }
  }, [messages, open]);

  function handleOption(option: Option) {
    const userMsg: Message = { id: nextId(), from: "user", text: option.label };
    const faq = FAQS[option.value];

    if (!faq) return;

    const botMsg = buildBotMessage(faq.answer, faq.followUp);

    setMessages((prev) => [...prev, userMsg, botMsg]);

    if (option.value === "contact") {
      setShowEmailCard(true);
    } else {
      setShowEmailCard(false);
    }
  }

  const emailSubject = encodeURIComponent("Support Request — Sonic Express Services");
  const emailBody = encodeURIComponent(
    "Hello Sonic Express Support,\n\nI need assistance with my shipment.\n\nTracking Number (if applicable): \n\nPlease describe your issue:\n\n"
  );

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center rounded-full bg-emerald-500 hover:bg-emerald-600 text-white cursor-pointer transition-all"
        style={{ boxShadow: "0 4px 24px rgba(52,211,153,0.45)" }}
        aria-label="Open support chat"
      >
        <div className="w-6 h-6 flex items-center justify-center">
          {open
            ? <i className="ri-close-line text-xl" />
            : <i className="ri-customer-service-2-line text-xl" />
          }
        </div>
        {!open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 border-2 border-white flex items-center justify-center">
            <span className="text-white text-[8px] font-bold">1</span>
          </span>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[340px] max-w-[calc(100vw-24px)] flex flex-col rounded-2xl overflow-hidden border border-slate-200"
          style={{ height: "520px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-3 flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 shrink-0">
              <i className="ri-robot-2-line text-white text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-bold leading-tight">Sonic Assistant</p>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                <p className="text-emerald-100 text-xs">Online — typically replies instantly</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/20 text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto bg-slate-50 px-3 py-4 space-y-4">
            {messages.map((msg) => (
              <div key={msg.id}>
                {/* Bubble */}
                <div className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"} mb-1`}>
                  {msg.from === "bot" && (
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 shrink-0 mr-2 mt-0.5">
                      <i className="ri-robot-2-line text-xs" />
                    </div>
                  )}
                  <div
                    className={`max-w-[78%] px-3 py-2.5 rounded-2xl text-sm leading-relaxed
                      ${msg.from === "bot"
                        ? "bg-white border border-slate-100 text-slate-700 rounded-tl-sm"
                        : "bg-emerald-500 text-white rounded-tr-sm"
                      }`}
                  >
                    {msg.text}
                  </div>
                </div>

                {/* Email card (shown after contact option) */}
                {msg.from === "bot" && showEmailCard && msg === messages[messages.length - 1] && (
                  <div className="ml-8 mt-2 mb-1">
                    <a
                      href={`mailto:${SUPPORT_EMAIL}?subject=${emailSubject}&body=${emailBody}`}
                      className="flex items-center gap-3 bg-white border border-emerald-200 hover:border-emerald-400 rounded-xl px-3 py-2.5 transition-colors cursor-pointer group"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 group-hover:bg-emerald-200 transition-colors shrink-0">
                        <i className="ri-mail-send-line text-sm" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-700">Email Support</p>
                        <p className="text-xs text-slate-400 truncate">{SUPPORT_EMAIL}</p>
                      </div>
                      <div className="w-4 h-4 flex items-center justify-center text-slate-300 group-hover:text-emerald-500 transition-colors">
                        <i className="ri-arrow-right-s-line" />
                      </div>
                    </a>
                    <p className="text-xs text-slate-400 mt-1.5 pl-1">Opens your email app with details pre-filled.</p>
                  </div>
                )}

                {/* Option chips */}
                {msg.from === "bot" && msg.options && msg === messages[messages.length - 1] && (
                  <div className="ml-8 mt-2 flex flex-col gap-1.5">
                    {msg.options.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => handleOption(opt)}
                        className="text-left text-xs px-3 py-2 rounded-xl border border-emerald-200 bg-white hover:bg-emerald-50 hover:border-emerald-400 text-slate-600 hover:text-emerald-700 transition-colors cursor-pointer font-medium"
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Footer hint */}
          <div className="bg-white border-t border-slate-100 px-4 py-2.5 shrink-0">
            <p className="text-xs text-slate-400 text-center">
              Select an option above or{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=${emailSubject}&body=${emailBody}`}
                className="text-emerald-600 hover:underline font-medium cursor-pointer"
              >
                email us directly
              </a>
            </p>
          </div>
        </div>
      )}
    </>
  );
}
